import {Badge, Box, Button, Flex, Heading, Icon, Image, Text} from "@chakra-ui/react";
import {SortableHandle} from "react-sortable-hoc";
import {useState} from "react";
import {TbGridDots} from "react-icons/tb";
import {ExternalLinkIcon} from "@chakra-ui/icons";
import {FiEdit} from "react-icons/fi";
import {BsGift, BsGiftFill} from "react-icons/bs";
import {useUIManager} from "../hooks/useUIManager";

const Handle = SortableHandle(() => (
  <Box
    bg="blackAlpha.300"
    color="white"
    borderRadius="md"
    aria-label="Drag"
    position="absolute"
    top="0.75rem"
    left="0.75rem"
    cursor="grab"
    p={1}
    zIndex={2}
  >
    <TbGridDots/>
  </Box>
))

const EditBtn = ({onClick}: {onClick: () => any}) => <Box
  bg="blackAlpha.300"
  color="white"
  borderRadius="md"
  aria-label="Drag"
  position="absolute"
  top="0.75rem"
  right="0.75rem"
  cursor="pointer"
  p={1}
  zIndex={2}
  onClick={onClick}
>
  <FiEdit/>
</Box>

export interface CardProps {
  uuid: string;
  image: string;
  title: string;
  description?: string;
  link: string | null;
  hasHandle: boolean;
  editable: boolean;
  booked?: boolean;
  bookedByMe?: boolean;
  bookedBy?: string | null;
  /** Show the "Book" button (booking is available here and the viewer is not the owner). */
  canBook?: boolean;
  /** Show the "Cancel booking" button (the viewer booked it, or owns the wishlist and sees it). */
  canUnbook?: boolean;
  bookingBusy?: boolean;
  onBook?: () => void;
  onUnbook?: () => void;
}

export const Card = ({
                       uuid, image, title, description, link, hasHandle, editable,
                       booked, bookedByMe, bookedBy, canBook, canUnbook, bookingBusy, onBook, onUnbook
                     }: CardProps) => {
  const uiManager = useUIManager();
  const [showHandle, toggleHandle] = useState(false);

  const bookingLabel = bookedByMe
    ? 'Booked by you'
    : (bookedBy ? `Booked by ${bookedBy}` : 'Booked');

  return <>
    <Box
      bg="white"
      w="100%"
      maxW={{base: "100%", sm: "100%", md: "49%"}}
      p={3}
      borderRadius="lg"
      position="relative"
      display="flex"
      flexDirection="column"
      onMouseEnter={() => toggleHandle(true)}
      onMouseLeave={() => toggleHandle(false)}
    >
      {hasHandle && showHandle && <Handle/>}
      {editable && showHandle && <EditBtn onClick={() => uiManager.toggleEditItemModal(true, uuid)}/>}
      <Image
        src={image}
        fallbackSrc={require("../assets/placeholder.png")}
        objectFit="contain"
        w="100%"
        maxH={{base: "200px", md: "300px"}}
        mb={{base: 3, md: 5}}
      />

      <Flex justifyContent="space-between" alignItems="center">
        <Heading as="h2" size={{base: "sm", sm: "sm"}} flexWrap="wrap">{title.length === 35 ? title : `${title.substring(0, 32).trimEnd()}${title.length > 35 ? '...' : ''}`}</Heading>
        {!!link && (<Button as="a" href={link} target="_blank" colorScheme="gray" size="sm">View <ExternalLinkIcon ms={1}/></Button>)}
      </Flex>
      {!!description && (<Text fontSize={{base: "xs", sm: "sm"}} mt={2}>{description}</Text>)}

      {(booked || canBook || canUnbook) && (
        <Box mt="auto" pt={3}>
          {booked && (
            <Flex alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Badge colorScheme={bookedByMe ? 'green' : 'gray'} display="flex" alignItems="center" gap={1} px={2} py={1}
                     borderRadius="md" textTransform="none" maxW="100%">
                <Icon as={BsGiftFill}/>
                <Text noOfLines={1}>{bookingLabel}</Text>
              </Badge>
              {canUnbook && (
                <Button size="sm" variant="ghost" colorScheme="red" isLoading={bookingBusy}
                        onClick={onUnbook}>Cancel booking</Button>
              )}
            </Flex>
          )}

          {canBook && !booked && (
            <Button w="full" size="md" colorScheme="brand" variant="outline" leftIcon={<BsGift/>}
                    isLoading={bookingBusy} onClick={onBook}>Book</Button>
          )}
        </Box>
      )}
    </Box>
  </>
}
